import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import EditCable from "./cableSubscriptionEdit";
import { API_URL } from "../components/api";
import Checkbox from "@mui/material/Checkbox";
import CustomizedActions from "./BulkActions";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
} from "@mui/x-data-grid";
import AuthContext from "../context/AuthContext";
import axios from "axios";
import ExcelSVG from "../../ExcelSVG.svg";
import * as XLSX from "xlsx";
import Button from "react-bootstrap/Button";

function CableTable() {
  const [data, setData] = useState([]);
  const [checkedRows, setCheckedRows] = useState([]);
  const [showo, setShowo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { user } = useContext(AuthContext);
  const [DealerDetails, setDealerDetails] = useState({
    cable_id: 0,
    notes: 0,
    fullname_en: 0,
    expiry: 0,
    pack: 0,
    username: 0,
  });

  const handleshowo = () => setShowo(true);
  const handleedit = () => setShowo(false);
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(0);

  const updateDealerDetails = (details) => {
    setDealerDetails(details);
  };

  const [filters, setFilters] = useState({
    status: "",
    username: "",
    fullname_en: "",
    notes: "",
    pack: "",
    expiry: "",
    sub_id: "",
  });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [gridFilterModel, setGridFilterModel] = useState({
    items: [],
  });

  useEffect(() => {
    const controller = new AbortController();
    let alive = true;
    const fetchData = async () => {
      try {
        const authTokens = JSON.parse(localStorage.getItem("authTokens"));
        const response = await axios.get(`${API_URL}/radius/subscriptions/cable/`, {
          headers: { Authorization: `Bearer ${authTokens.access}` },
          params: { username: user.username },
          signal: controller.signal,
        });
        if (alive && response.status === 200) {
          setData(response.data);
        }
      } catch (error) {
        if (!axios.isCancel?.(error)) console.error(error);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchData();
    return () => {
      alive = false;
      controller.abort();
    };
  }, [user.username]);

  const handleDeselectCheckboxes = () => setCheckedRows([]);

  const handleCheckboxChange = (event, subscriptionId) => {
    const checked = event.target.checked;
    setCheckedRows((prev) =>
      checked ? [...prev, subscriptionId] : prev.filter((id) => id !== subscriptionId)
    );
  };

  const paginatedRows = useCallback(() => {
    const start = currentPage * pageSize;
    const end = start + pageSize;
    return filteredRows.slice(start, end);
  }, [currentPage, pageSize]);

  const applyAllFilters = (rows, filtersArg, searchArg, gridModelArg) => {
    return rows.filter((row) => {
      const searchMatch = searchArg
        .split(" ")
        .every((word) =>
          Object.values(row).some((value) =>
            String(value).toLowerCase().includes(word.toLowerCase().trim())
          )
        );
      const modalFilterMatch = Object.keys(filtersArg).every((key) => {
        if (!filtersArg[key]) return true;
        const rowValue = String(row[key] || "").toLowerCase();
        const filterValue = String(filtersArg[key]).toLowerCase();
        return rowValue.includes(filterValue);
      });
      const gridFilterMatch = (gridModelArg.items ?? []).every((fi) => {
        if (!fi.value) return true;
        const cellValue = row[fi.field];
        const v = fi.value;
        switch (fi.operator) {
          case "contains":
            return String(cellValue).toLowerCase().includes(String(v).toLowerCase());
          case "equals":
            return String(cellValue) === String(v);
          case "startsWith":
            return String(cellValue).toLowerCase().startsWith(String(v).toLowerCase());
          case "endsWith":
            return String(cellValue).toLowerCase().endsWith(String(v).toLowerCase());
          default:
            return true;
        }
      });
      return searchMatch && modalFilterMatch && gridFilterMatch;
    });
  };

  const filteredRows = useMemo(
    () => applyAllFilters(data, filters, searchQuery, gridFilterModel),
    [data, filters, searchQuery, gridFilterModel]
  );

  const areAllRowsSelected = () => {
    const currentViewRows = paginatedRows();
    const currentViewIds = currentViewRows.map((row) => row.cable_subscription_id);
    return currentViewIds.every((id) => checkedRows.includes(id));
  };

  const handleSelectAllCheckboxes = () => {
    const currentViewRows = paginatedRows();
    const currentViewIds = currentViewRows.map((row) => row.cable_subscription_id);
    if (areAllRowsSelected()) {
      setCheckedRows(checkedRows.filter((id) => !currentViewIds.includes(id)));
    } else {
      setCheckedRows([
        ...checkedRows,
        ...currentViewIds.filter((id) => !checkedRows.includes(id)),
      ]);
    }
  };

  const columns = [
    {
      field: "checkbox",
      headerName: "",
      width: 100,
      renderCell: (params) => (
        <Checkbox
          className="subcheck"
          checked={checkedRows.includes(params.row.cable_subscription_id)}
          onChange={(event) =>
            handleCheckboxChange(event, params.row.cable_subscription_id)
          }
        />
      ),
    },
    { field: "status", headerName: "Status", flex: 2, minWidth: 50 },
    { field: "username", headerName: "Username", flex: 2, minWidth: 50 },
    { field: "fullname_en", headerName: "Client", flex: 2, minWidth: 50 },
    { field: "notes", headerName: "Notes", flex: 2, minWidth: 50 },
    { field: "package", headerName: "Package", flex: 2, minWidth: 50 },
    {
      field: "expiration_date",
      headerName: "Expiry",
      flex: 1,
      minWidth: 100,
      valueFormatter: (params) => {
        const date = new Date(params.value);
        const day = date.getDate().toString().padStart(2, "0");
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      },
    },
    {
      field: "creation_date",
      headerName: "Creation",
      flex: 1,
      minWidth: 100,
      valueFormatter: (params) => {
        const date = new Date(params.value);
        const day = date.getDate().toString().padStart(2, "0");
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 100,
      flex: 2,
      renderCell: (params) => (
        <div>
          <button
            className="edit-client"
            onClick={() => {
              const details = {
                cable_subscription_id: params.row.cable_subscription_id,
                username: params.row.username,
                fullname_en: params.row.fullname_en,
                notes: params.row.notes,
                pack: params.row.package,
                expiry: params.row.expiration_date,
                sub_id: params.row.subscription_id,
              };
              updateDealerDetails(details);
              handleshowo();
            }}
          >
            Edit
          </button>
        </div>
      ),
    },
  ];

  const getRowId = (row) => row.cable_subscription_id;

  useEffect(() => {
    const searchValue = sessionStorage.getItem("searchSubscriptionServices");
    Object.keys(sessionStorage).forEach((key) => {
      if (key !== "searchSubscriptionServices") sessionStorage.removeItem(key);
    });
    if (searchValue) {
      sessionStorage.setItem("searchSubscriptionServices", searchValue);
      setSearchQuery(searchValue);
    }
  }, []);

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    sessionStorage.setItem("searchSubscriptionServices", query);
  };

  const columnHeaders = {
    status: "Status",
    username: "Username",
    fullname_en: "Client",
    package: "Package",
    notes: "Notes",
    expiration_date: "Expiry",
    creation_date: "Creation",
  };

  const transformRowsForExport = (rows) => {
    return rows.map((row) => {
      const transformedRow = {};
      Object.keys(columnHeaders).forEach((key) => {
        transformedRow[columnHeaders[key]] = row[key];
      });
      return transformedRow;
    });
  };

  const handleExportToExcel = () => {
    const transformedRows = transformRowsForExport(filteredRows);
    const worksheet = XLSX.utils.json_to_sheet(transformedRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Subscription Services");
    XLSX.writeFile(workbook, "Subscription Service.xlsx");
  };

  const [isPressed, setIsPressed] = useState(false);

  const handleExportClick = () => {
    setIsPressed(true);
    handleExportToExcel();
    setTimeout(() => setIsPressed(false), 200);
  };

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport />
      <Button
        onClick={handleExportClick}
        variant="outline-success"
        style={{
          display: "flex",
          alignItems: "center",
          padding: "5px 10px",
          fontSize: "14px",
          border: "none",
          backgroundColor: isPressed ? "white" : "transparent",
          color: isPressed ? "black" : "#007bff",
          cursor: "pointer",
        }}
      >
        <img
          src={ExcelSVG}
          alt="Excel Icon"
          style={{
            width: "20px",
            marginRight: "8px",
            filter: isPressed ? "invert(0)" : "invert(1)",
          }}
        />
        Excel
      </Button>
      <Button onClick={() => setShowFilterModal(true)} variant="outline-primary">
        Filter
      </Button>
    </GridToolbarContainer>
  );

  return (
    <div className="ClientTable">
      <div className="actions">
       <div className="mt-4">
  <CustomizedActions
    subscriptions={checkedRows}
    deselect={handleDeselectCheckboxes}
  />
</div>
         <input
        type="text"
        placeholder="Search..."
        value={searchQuery}
        onChange={handleSearch}
        className="search-client"
      />
      </div>
      {!loading && data.length > 0 && (
        <div style={{ height: 520, width: "100%" }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={getRowId}
            components={{ Toolbar: CustomToolbar }}
            rowsPerPageOptions={[10, 100, 1000]}
            pagination
            disableSelectionOnClick
            filterModel={gridFilterModel}
            onFilterModelChange={setGridFilterModel}
          />
        </div>
      )}
      <Modal show={showo} onHide={handleedit}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Cable Subscription</Modal.Title>
        </Modal.Header>
        <EditCable
          cable_id={DealerDetails.cable_subscription_id}
          fullname={DealerDetails.fullname_en}
          pack={DealerDetails.pack}
          notes={DealerDetails.notes}
          expiry={DealerDetails.expiry}
          username={DealerDetails.username}
        />
      </Modal>
      <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Filters</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {columns
            .filter((col) => col.field !== "actions")
            .map((col) => (
              <div key={col.field} className="filter">
                <label>{col.headerName}</label>
                <input
                  type="text"
                  placeholder={`Filter by ${col.headerName}`}
                  value={filters[col.field] || ""}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, [col.field]: e.target.value }))
                  }
                  className="filter-input"
                />
              </div>
            ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFilterModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default CableTable;
